import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { LOGIN_PATH } from "@/const";
import { trpc } from "@/lib/trpc";
import { Calendar, Award, Clock, Download, Loader2, ArrowLeft, Trophy, AlertTriangle } from "lucide-react";
import { useState } from "react";
import { Link, useLocation } from "wouter";
import { toast } from "sonner";

function downloadCertificate(userName: string, levelName: string, issuedAt: string, expiresAt: string | null) {
  const canvas = document.createElement("canvas");
  canvas.width = 1200;
  canvas.height = 800;
  const ctx = canvas.getContext("2d")!;

  const grad = ctx.createLinearGradient(0, 0, 1200, 800);
  grad.addColorStop(0, "#f8fafc");
  grad.addColorStop(1, "#e0e7ff");
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, 1200, 800);

  ctx.strokeStyle = "#6366f1";
  ctx.lineWidth = 8;
  ctx.strokeRect(30, 30, 1140, 740);
  ctx.strokeStyle = "#c7d2fe";
  ctx.lineWidth = 2;
  ctx.strokeRect(50, 50, 1100, 700);

  ctx.fillStyle = "#312e81";
  ctx.font = "bold 48px serif";
  ctx.textAlign = "center";
  ctx.fillText("認 定 証", 600, 140);

  ctx.fillStyle = "#6366f1";
  ctx.font = "20px sans-serif";
  ctx.fillText("Certificate of Achievement", 600, 175);

  ctx.fillStyle = "#1e1b4b";
  ctx.font = "bold 40px sans-serif";
  ctx.fillText(userName, 600, 300);

  ctx.fillStyle = "#4338ca";
  ctx.font = "24px sans-serif";
  ctx.fillText("上記の者は以下の試験に合格したことを証します", 600, 370);

  ctx.fillStyle = "#312e81";
  ctx.font = "bold 36px sans-serif";
  ctx.fillText(levelName, 600, 450);

  ctx.fillStyle = "#64748b";
  ctx.font = "18px sans-serif";
  ctx.fillText(`発行日: ${new Date(issuedAt).toLocaleDateString("ja-JP")}`, 600, 530);
  if (expiresAt) {
    ctx.fillText(`有効期限: ${new Date(expiresAt).toLocaleDateString("ja-JP")}`, 600, 560);
  }

  ctx.fillStyle = "#4338ca";
  ctx.font = "bold 28px sans-serif";
  ctx.fillText("Biz-AI", 600, 680);
  ctx.fillStyle = "#64748b";
  ctx.font = "16px sans-serif";
  ctx.fillText("資格認定プラットフォーム", 600, 710);

  const link = document.createElement("a");
  link.download = `certificate_${levelName}_${new Date().toISOString().slice(0, 10)}.png`;
  link.href = canvas.toDataURL("image/png");
  link.click();
}

export default function MyPage() {
  const { user, loading: authLoading } = useAuth();
  const [, setLocation] = useLocation();
  const [confirmStartRegistrationId, setConfirmStartRegistrationId] = useState<number | null>(null);
  const [termsAgreed, setTermsAgreed] = useState(false);
  
  const { data: registrations, isLoading: registrationsLoading } = trpc.examRegistrations.myRegistrations.useQuery(
    undefined,
    { enabled: !!user }
  );
  
  const { data: attempts, isLoading: attemptsLoading } = trpc.examAttempts.myAttempts.useQuery(
    undefined,
    { enabled: !!user }
  );

  const { data: badges, isLoading: badgesLoading } = trpc.badges.myBadges.useQuery(
    undefined,
    { enabled: !!user }
  );

  const { data: levels } = trpc.examLevels.list.useQuery();
  const { data: sessions } = trpc.examSessions.list.useQuery();

  const logoutMutation = trpc.auth.logout.useMutation({
    onSuccess: () => {
      toast.success("ログアウトしました");
      setLocation("/");
    },
  });

  const handleLogout = () => {
    logoutMutation.mutate();
  };

  const handleConfirmStartExam = () => {
    if (!confirmStartRegistrationId) return;
    if (!termsAgreed) {
      toast.error("規約への同意が必要です");
      return;
    }
    setLocation(`/exam/${confirmStartRegistrationId}`);
    setConfirmStartRegistrationId(null);
    setTermsAgreed(false);
  };

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50">
        <Card className="max-w-md w-full">
          <CardHeader>
            <CardTitle>ログインが必要です</CardTitle>
            <CardDescription>
              マイページを表示するには、ログインしてください。
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Link href={LOGIN_PATH}>
              <Button className="w-full">ログイン</Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  const getLevelName = (levelId: number) => {
    return levels?.find(l => l.id === levelId)?.displayName || "不明";
  };

  const getSessionName = (sessionId: number | null) => {
    if (!sessionId) return "不明";
    const session = sessions?.find(s => s.id === sessionId);
    if (!session) return "不明";
    return session.displayName || session.name || getLevelName(session.levelId);
  };

  const getSessionLevelName = (sessionId: number | null) => {
    if (!sessionId) return "不明";
    const session = sessions?.find(s => s.id === sessionId);
    if (!session) return "不明";
    return getLevelName(session.levelId);
  };

  const getExamAvailability = (sessionId: number | null): { available: boolean; message: string } => {
    if (!sessionId) return { available: false, message: "" };
    const session = sessions?.find(s => s.id === sessionId);
    if (!session) return { available: false, message: "" };

    const now = new Date();
    const jstFormatter = new Intl.DateTimeFormat("ja-JP", {
      timeZone: "Asia/Tokyo",
      year: "numeric", month: "2-digit", day: "2-digit",
      hour: "2-digit", minute: "2-digit", hour12: false,
    });
    const parts = jstFormatter.formatToParts(now);
    const getPart = (type: string) => parts.find(p => p.type === type)?.value || "";
    const todayStr = `${getPart("year")}-${getPart("month")}-${getPart("day")}`;
    const nowTimeStr = `${getPart("hour")}:${getPart("minute")}`;

    const isSuperAdmin = user?.email && ["info@aim-llc.jp", "test23@gmail.com"].includes(user.email);

    if (!isSuperAdmin && session.examDate && todayStr !== session.examDate) {
      return { available: false, message: `試験日: ${session.examDate}` };
    }
    if (!isSuperAdmin && session.examStartTime && nowTimeStr < session.examStartTime) {
      return { available: false, message: `開始時間: ${session.examStartTime}` };
    }
    let calculatedEndTime: string | null = null;
    if (session.examStartTime && session.examAccessHours) {
      const [startH, startM] = session.examStartTime.split(":").map(Number);
      const endH = startH + session.examAccessHours;
      calculatedEndTime = `${String(endH).padStart(2, "0")}:${String(startM).padStart(2, "0")}`;
    } else if (session.examEndTime) {
      calculatedEndTime = session.examEndTime;
    }
    if (!isSuperAdmin && calculatedEndTime && nowTimeStr > calculatedEndTime) {
      return { available: false, message: `終了時間: ${calculatedEndTime}` };
    }
    return { available: true, message: "" };
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50">
      <header className="bg-white/80 backdrop-blur-sm border-b border-purple-100">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <Link href="/">
            <Button variant="ghost" className="gap-2">
              <ArrowLeft className="w-4 h-4" />
              トップページ
            </Button>
          </Link>
          <div className="flex gap-2 items-center">
            <span className="text-sm text-gray-600">{user.name}さん</span>
            <Button variant="outline" onClick={handleLogout}>
              ログアウト
            </Button>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-12">
        <div className="max-w-5xl mx-auto space-y-8">
          <div className="text-center space-y-2">
            <h1 className="text-4xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
              マイページ
            </h1>
            <p className="text-gray-600">受験履歴と取得バッジを確認できます</p>
          </div>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Trophy className="w-5 h-5 text-yellow-500" />
                取得バッジ
              </CardTitle>
            </CardHeader>
            <CardContent>
              {badgesLoading ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin" />
                </div>
              ) : badges && badges.length > 0 ? (
                <div className="grid md:grid-cols-2 gap-4">
                  {badges.map((badge) => {
                    const now = new Date();
                    const isExpired = badge.expiresAt ? new Date(badge.expiresAt) < now : false;
                    const daysUntilExpiry = badge.expiresAt ? Math.ceil((new Date(badge.expiresAt).getTime() - now.getTime()) / (1000 * 60 * 60 * 24)) : null;
                    const isNearExpiry = daysUntilExpiry !== null && daysUntilExpiry > 0 && daysUntilExpiry <= 30;
                    return (
                      <Card key={badge.id} className={
                        isExpired
                          ? "bg-gradient-to-br from-gray-100 to-gray-200 border-gray-300 opacity-70"
                          : isNearExpiry
                            ? "bg-gradient-to-br from-amber-50 to-orange-50 border-amber-300"
                            : "bg-gradient-to-br from-yellow-50 to-orange-50 border-yellow-200"
                      }>
                        <CardContent className="p-6">
                          <div className="flex items-center gap-4">
                            <div className={`w-16 h-16 rounded-full flex items-center justify-center ${
                              isExpired
                                ? "bg-gradient-to-br from-gray-400 to-gray-500"
                                : "bg-gradient-to-br from-yellow-400 to-orange-500"
                            }`}>
                              <Award className="w-8 h-8 text-white" />
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <div className={`font-bold text-lg ${isExpired ? "text-gray-500" : ""}`}>
                                  {getLevelName(badge.levelId)}
                                </div>
                                {isExpired && (
                                  <Badge variant="destructive" className="text-xs">期限切れ</Badge>
                                )}
                                {isNearExpiry && (
                                  <Badge variant="outline" className="text-xs border-amber-500 text-amber-600">
                                    <AlertTriangle className="w-3 h-3 mr-1" />
                                    残り{daysUntilExpiry}日
                                  </Badge>
                                )}
                              </div>
                              <div className="text-sm text-gray-600">
                                発行日: {new Date(badge.issuedAt).toLocaleDateString("ja-JP")}
                              </div>
                              {badge.expiresAt && (
                                <div className={`text-sm ${isExpired ? "text-red-500 font-medium" : "text-gray-500"}`}>
                                  有効期限: {new Date(badge.expiresAt).toLocaleDateString("ja-JP")}
                                  {isExpired && " （無効）"}
                                </div>
                              )}
                              {!badge.expiresAt && (
                                <div className="text-sm text-green-600">無期限</div>
                              )}
                              {!isExpired && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="mt-2"
                                  onClick={() => downloadCertificate(
                                    user?.name || "",
                                    getLevelName(badge.levelId),
                                    badge.issuedAt,
                                    badge.expiresAt
                                  )}
                                >
                                  <Download className="w-3 h-3 mr-1" />
                                  認定証ダウンロード
                                </Button>
                              )}
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  まだバッジを取得していません
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calendar className="w-5 h-5" />
                受験申込
              </CardTitle>
            </CardHeader>
            <CardContent>
              {registrationsLoading ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin" />
                </div>
              ) : (() => {
                const notStartedRegistrations = registrations?.filter(r =>
                  r.status === "pending" && !attempts?.some(a => a.registrationId === r.id)
                ) || [];
                return notStartedRegistrations.length > 0 ? (
                <div className="space-y-4">
                  {notStartedRegistrations.map((registration) => (
                    <Card key={registration.id} className="border-l-4 border-l-indigo-500">
                      <CardContent className="p-4 sm:p-6">
                        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-3">
                          <div className="space-y-2">
                            <div className="font-bold text-base sm:text-lg">{getSessionName(registration.sessionId)}</div>
                            <div className="text-sm text-gray-600">
                              申込日: {new Date(registration.createdAt).toLocaleDateString("ja-JP")}
                            </div>
                            <Badge variant="secondary">受付中</Badge>
                          </div>
                          {(() => {
                            const availability = getExamAvailability(registration.sessionId);
                            const session = sessions?.find(s => s.id === registration.sessionId);
                            return (
                              <div className="text-left sm:text-right space-y-2">
                                {session?.examDate && (
                                  <div className="text-xs text-gray-500 whitespace-nowrap">
                                    <span className="whitespace-nowrap">試験日: {session.examDate}</span>
                                    {session.examStartTime && (
                                      <span className="whitespace-nowrap"> {session.examStartTime}〜{
                                        session.examAccessHours
                                          ? (() => {
                                              const [h, m] = session.examStartTime!.split(":").map(Number);
                                              return `${String(h + session.examAccessHours).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
                                            })()
                                          : session.examEndTime || ""
                                      }</span>
                                    )}
                                  </div>
                                )}
                                {availability.available ? (
                                  <Button onClick={() => setConfirmStartRegistrationId(registration.id)}>
                                    受験開始
                                  </Button>
                                ) : (
                                  <div>
                                    <Button disabled variant="secondary">受験時間外</Button>
                                    <div className="text-xs text-red-500 mt-1">{availability.message}</div>
                                  </div>
                                )}
                              </div>
                            );
                          })()}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  受験申込がありません
                  <div className="mt-4">
                    <Link href="/register">
                      <Button>受験申込をする</Button>
                    </Link>
                  </div>
                </div>
              );
              })()}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Clock className="w-5 h-5" />
                受験履歴
              </CardTitle>
            </CardHeader>
            <CardContent>
              {attemptsLoading ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin" />
                </div>
              ) : attempts && attempts.length > 0 ? (
                <div className="space-y-4">
                  {attempts.map((attempt) => (
                    <Card key={attempt.id} className={`border-l-4 ${!attempt.completedAt ? "border-l-yellow-500" : attempt.passed ? "border-l-green-500" : "border-l-red-500"}`}>
                      <CardContent className="p-6">
                        <div className="flex justify-between items-start">
                          <div className="space-y-2">
                            <div className="font-bold text-lg">{getSessionLevelName(attempt.sessionId)}</div>
                            <div className="text-sm text-gray-600">
                              受験日: {new Date(attempt.startedAt).toLocaleDateString("ja-JP")}
                            </div>
                            {attempt.completedAt && attempt.score !== null ? (
                              <div className="flex items-center gap-4">
                                <span className="text-2xl font-bold">{attempt.score}点</span>
                                <Badge variant={attempt.passed ? "default" : "destructive"}>
                                  {attempt.passed ? "合格" : "不合格"}
                                </Badge>
                              </div>
                            ) : !attempt.completedAt ? (
                              <Badge variant="outline" className="text-yellow-600 border-yellow-400">受験中</Badge>
                            ) : null}
                          </div>
                          {attempt.completedAt ? (
                            <Link href={`/result/${attempt.id}`}>
                              <Button variant="outline">詳細を見る</Button>
                            </Link>
                          ) : (
                            <Link href={`/exam/${attempt.registrationId}`}>
                              <Button>再開</Button>
                            </Link>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  受験履歴がありません
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      <AlertDialog
        open={confirmStartRegistrationId !== null}
        onOpenChange={(open) => {
          if (!open) {
            setConfirmStartRegistrationId(null);
            setTermsAgreed(false);
          }
        }}
      >
        <AlertDialogContent className="max-w-[calc(100%-2rem)] sm:max-w-2xl max-h-[85vh] flex flex-col p-4 sm:p-6">
          <AlertDialogHeader className="flex-shrink-0">
            <AlertDialogTitle className="text-lg sm:text-xl">試験受験規約</AlertDialogTitle>
            <AlertDialogDescription className="pb-1 text-xs sm:text-sm">
              試験を開始する前に、以下の規約をよくお読みいただき、同意の上で開始してください。
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="flex-1 min-h-0 overflow-y-auto border rounded-md p-3 sm:p-4 bg-gray-50 space-y-3 text-xs sm:text-sm">
            <div>
              <h4 className="font-semibold text-gray-900 mb-1">1. 試験時間について</h4>
              <p className="text-gray-700">受験を開始するとタイマーが進行します。一度開始すると、試験時間内に終了する必要があります。時間内に終了しなかった場合、未回答の問題は不正解となります。</p>
            </div>
            <div>
              <h4 className="font-semibold text-gray-900 mb-1">2. ブラウザの再入室について</h4>
              <p className="text-gray-700">試験の途中で誤ってブラウザを閉じた場合は、再度ログインして試験を再開することができます。ただし、何度も退出を繰り返すと、システム保護のため再入室できなくなることがあります。安定した環境で受験してください。</p>
            </div>
            <div>
              <h4 className="font-semibold text-gray-900 mb-1">3. 返金について</h4>
              <p className="text-gray-700">いかなる場合においても、入室後に試験時間内に試験が終了しなかった場合、通信障害、機器の不具合、その他の理由により試験が完了できなかった場合など、受験料の返金はいたしかねます。</p>
            </div>
            <div>
              <h4 className="font-semibold text-gray-900 mb-1">4. 不正行為の禁止</h4>
              <p className="text-gray-700">以下の行為は不正行為とみなされます：</p>
              <ul className="list-disc list-inside ml-2 mt-1 space-y-1 text-gray-700">
                <li>画面のスクリーンショットを撮影すること</li>
                <li>試験問題を第三者に共有・漏洩すること</li>
                <li>他者の解答を参照すること（カンニング）</li>
                <li>複数のデバイスで同時に受験すること</li>
                <li>第三者に代理受験させること</li>
                <li>その他、公正な試験を妨げる行為</li>
              </ul>
              <p className="text-gray-700 mt-1">不正行為が検知された場合、合格点数を取得していても資格が剥奪され、今後の受験が制限されることがあります。</p>
            </div>
            <div>
              <h4 className="font-semibold text-gray-900 mb-1">5. 受験環境について</h4>
              <p className="text-gray-700">安定したインターネット接続環境で受験してください。通信障害による試験の中断や未提出については、当方では責任を負いかねます。</p>
            </div>
            <div>
              <h4 className="font-semibold text-gray-900 mb-1">6. 本人確認</h4>
              <p className="text-gray-700">登録されたアカウントの本人のみが受験できます。他者への受験権の譲渡や貸与は禁止されています。</p>
            </div>
          </div>
          <div className="flex items-start space-x-2 pt-3 flex-shrink-0">
            <Checkbox
              id="terms"
              checked={termsAgreed}
              onCheckedChange={(checked) => setTermsAgreed(checked === true)}
              className="mt-0.5"
            />
            <label
              htmlFor="terms"
              className="text-xs sm:text-sm font-medium leading-tight peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
            >
              上記の受験規約を確認し、同意します
            </label>
          </div>
          <AlertDialogFooter className="flex-shrink-0 flex-col gap-2 sm:flex-row pt-1">
            <AlertDialogCancel onClick={() => setTermsAgreed(false)} className="w-full sm:w-auto">キャンセル</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmStartExam} disabled={!termsAgreed} className="w-full sm:w-auto">
              同意して試験を開始
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
